// Cluster-attraction force — FXC-8538 (Warp).
//
// A d3-force-compatible force that pulls each node toward the centroid of
// its cluster. Combined with the simulation's default many-body
// repulsion, same-cluster nodes draw together while clusters drift apart
// — so cliques/communities visibly separate into their hulls instead of
// collapsing into one blob. Plain function matching d3's force interface
// (no dependency): `force(alpha)` + `force.initialize(nodes)`.

interface SimNode {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  cluster?: string | number;
}

export interface ClusterForce {
  (alpha: number): void;
  initialize(nodes: SimNode[]): void;
}

/** `strength` ∈ ~[0,1]; higher pulls clusters tighter. */
export function clusterForce(strength = 0.12): ClusterForce {
  let nodes: SimNode[] = [];

  const force = ((alpha: number) => {
    if (nodes.length === 0) return;
    // Per-cluster centroid.
    const sx = new Map<string | number, number>();
    const sy = new Map<string | number, number>();
    const cnt = new Map<string | number, number>();
    for (const n of nodes) {
      if (n.cluster == null) continue;
      sx.set(n.cluster, (sx.get(n.cluster) ?? 0) + (n.x ?? 0));
      sy.set(n.cluster, (sy.get(n.cluster) ?? 0) + (n.y ?? 0));
      cnt.set(n.cluster, (cnt.get(n.cluster) ?? 0) + 1);
    }
    const k = strength * alpha;
    for (const n of nodes) {
      if (n.cluster == null) continue;
      const c = cnt.get(n.cluster) ?? 1;
      const cx = (sx.get(n.cluster) ?? 0) / c;
      const cy = (sy.get(n.cluster) ?? 0) / c;
      n.vx = (n.vx ?? 0) + (cx - (n.x ?? 0)) * k;
      n.vy = (n.vy ?? 0) + (cy - (n.y ?? 0)) * k;
    }
  }) as ClusterForce;

  force.initialize = (n: SimNode[]) => {
    nodes = n;
  };
  return force;
}
